import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, RefreshControl, TextInput, Platform } from 'react-native';
import { openExternal } from '../../../src/utils/openExternal';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api } from '../../../src/api/client';
import { makeOwnerApi } from '../../../src/api/adminAsOwner';
import { useAdminPermissions } from '../../../src/hooks/useAdminPermissions';
import { config } from '../../../src/config';
import ResetPasswordModal from '../../../src/components/ResetPasswordModal';
import StageBadge from '../../../src/components/StageBadge';
import InlineConfirm from '../../../src/components/InlineConfirm';
import InlineMessage from '../../../src/components/InlineMessage';
import Button from '../../../src/components/Button';
import { useToast } from '../../../src/components/Toast';
import { useTheme } from '../../../src/context/ThemeContext';
import { useNetwork } from '../../../src/context/NetworkContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';
import PageFooter from '../../../src/components/PageFooter';

interface OwnerDetail {
  id: string; accountNumber: string; email: string; businessName: string; phone: string | null;
  stripeAccountId: string | null; stripeOnboarded: boolean; isSuspended: boolean; createdAt: string;
  // Acquisition milestones — same definitions as Admin > Acquisition (backend/src/utils/ownerMilestones.ts).
  hasLocation: boolean; hasController: boolean; activated: boolean;
  activatedAt: string | null; firstRevenueAt: string | null;
  locations: { id: string; name: string; city: string; state: string; isActive: boolean }[];
  referral: { id: string; code: string; displayName: string; type: string; commissionRateBps: number | null; commissionDurationMonths: number | null } | null;
  referralAttributedAt: string | null;
  ownReferralCode: string | null;
  commissionRateBpsOverride: number | null;
  commissionDurationMonthsOverride: number | null;
  commissionOverrideSetAt: string | null;
}

/** 500 bps → "5%", 250 → "2.5%". */
const fmtRateBps = (bps: number) => `${(bps / 100).toFixed(2).replace(/\.?0+$/, '')}%`;

interface RevenueSums { totalCharged: number; totalFees: number; ownerRevenue: number; count: number; }
interface RevenueData {
  today: RevenueSums; thisWeek: RevenueSums; thisMonth: RevenueSums;
  lastMonth: RevenueSums; yearToDate: RevenueSums; allTime: RevenueSums;
  fleetToday: RevenueSums; fleetThisWeek: RevenueSums; fleetThisMonth: RevenueSums;
  fleetLastMonth: RevenueSums; fleetYearToDate: RevenueSums; fleetAllTime: RevenueSums;
}

/**
 * Owner-side dashboard shape — fetched via `makeOwnerApi` so the admin sees
 * exactly what the owner does (location stats, active sessions, ratings).
 * Mirrors the owner endpoint at backend/src/routes/analytics.ts:149.
 */
interface OwnerLocationStat {
  locationId: string;
  locationName: string;
  count: number;
  totalCharged: number;
  activeSessionsCount: number;
  averageRating: number | null;
  ratingCount: number;
}
interface OwnerDashboardData {
  locationStats: OwnerLocationStat[];
}

interface TxItem {
  id: string; type: string; amount: number; platformFee: number; status: string;
  paymentBrand: string | null; paymentLast4: string | null; createdAt: string;
}

const fmt = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

function RevenueCard({ label, data, fleet, styles }: { label: string; data: RevenueSums; fleet?: RevenueSums; styles: ReturnType<typeof createStyles> }) {
  return (
    <View style={styles.revenueCard}>
      <Text style={styles.revenueValue}>{fmt(data.totalCharged)}</Text>
      <Text style={styles.revenueLabel}>{label}</Text>
      <Text style={styles.revenueSub}>{data.count} washes</Text>
      {fleet && fleet.count > 0 && (
        <Text style={styles.revenueSub}>Fleet: {fmt(fleet.totalCharged)} ({fleet.count})</Text>
      )}
    </View>
  );
}

/** AdminOwnerDetail - owner profile, revenue breakdown, transactions, and account management. */
export default function AdminOwnerDetail() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { isOffline } = useNetwork();
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { has } = useAdminPermissions();
  const hasReferralWrite = has('platform_referrals', 'write');
  const [owner, setOwner] = useState<OwnerDetail | null>(null);
  const [revenue, setRevenue] = useState<RevenueData | null>(null);
  const [ownerDashboard, setOwnerDashboard] = useState<OwnerDashboardData | null>(null);
  const [transactions, setTransactions] = useState<TxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [inlineMsg, setInlineMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Referral attribution edit (platform_referrals:write)
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [referralInput, setReferralInput] = useState('');
  const [referralSaving, setReferralSaving] = useState(false);

  // Per-owner commission override edit (platform_referrals:write, mig 0073)
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideRateInput, setOverrideRateInput] = useState('');
  const [overrideDurationInput, setOverrideDurationInput] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Activation state
  const [activation, setActivation] = useState<any>(null);
  const [showWaiveForm, setShowWaiveForm] = useState(false);
  const [waiveType, setWaiveType] = useState<'months' | 'indefinite'>('indefinite');
  const [waiveMonths, setWaiveMonths] = useState('3');
  const [waiveNote, setWaiveNote] = useState('');
  const [activationLoading, setActivationLoading] = useState(false);

  /** Set (code string) or clear (null) this owner's referral attribution. */
  const handleReferralSave = async (code: string | null) => {
    setReferralSaving(true);
    try {
      await api(`/api/admin/owners/${id}/referral`, { method: 'PATCH', body: { code } });
      toast.show({ type: 'success', title: code ? 'Referral attribution saved' : 'Referral attribution cleared' });
      setShowReferralForm(false);
      fetchAll();
    } catch (err: any) {
      if (!isOffline) toast.show({ type: 'error', title: 'Referral update failed', message: err.message });
    } finally { setReferralSaving(false); }
  };

  /** Set or clear (rate null) this owner's commission override pair. */
  const handleOverrideSave = async (rateBps: number | null, durationMonths: number | null) => {
    setOverrideSaving(true);
    try {
      await api(`/api/admin/owners/${id}/commission-override`, { method: 'PATCH', body: { rateBps, durationMonths } });
      toast.show({ type: 'success', title: rateBps != null ? 'Commission override saved' : 'Commission override cleared' });
      setShowOverrideForm(false);
      fetchAll();
    } catch (err: any) {
      if (!isOffline) toast.show({ type: 'error', title: 'Override update failed', message: err.message });
    } finally { setOverrideSaving(false); }
  };

  const submitOverrideForm = () => {
    const rateTxt = overrideRateInput.trim();
    const durTxt = overrideDurationInput.trim();
    const pct = Number(rateTxt);
    if (!rateTxt || !Number.isFinite(pct) || pct <= 0 || pct > 100) {
      toast.show({ type: 'error', title: 'Rate must be a percentage between 0.01 and 100' });
      return;
    }
    let duration: number | null = null;
    if (durTxt) {
      const n = Number(durTxt);
      if (!Number.isInteger(n) || n < 1) {
        toast.show({ type: 'error', title: 'Duration must be whole months (blank = forever)' });
        return;
      }
      duration = n;
    }
    handleOverrideSave(Math.round(pct * 100), duration);
  };

  const fetchActivation = async (signal?: AbortSignal) => {
    try {
      const data = await api<any>(`/api/admin/owners/${id}/activation`);
      if (signal?.aborted) return;
      setActivation(data);
    } catch (err) { if (!(signal?.aborted)) console.error('fetch activation:', err); }
  };

  const fetchAll = async (signal?: AbortSignal) => {
    try {
      const ownerApi = makeOwnerApi(id);
      // `ownerDashboard` is best-effort: an admin missing
      // owner_panel.analytics:read will 403, in which case we degrade to the
      // admin-only revenue card.
      const [ownerData, revenueData, txData, ownerDashboardData] = await Promise.all([
        api<OwnerDetail>(`/api/admin/owners/${id}`),
        api<RevenueData>(`/api/admin/analytics/owner/${id}`),
        api<{ transactions: TxItem[]; total: number }>(`/api/admin/transactions?ownerId=${id}&limit=10`),
        ownerApi.get<OwnerDashboardData>('/api/analytics/dashboard').catch(() => null),
      ]);
      if (signal?.aborted) return;
      setOwner(ownerData);
      setRevenue(revenueData);
      setTransactions(txData.transactions);
      setOwnerDashboard(ownerDashboardData);
    } catch (err: any) {
      if (!(signal?.aborted) && !isOffline) {
        toast.show({ type: 'error', title: 'Failed to load owner', message: err?.message ?? 'Unknown error' });
      }
    }
    finally { if (!(signal?.aborted)) { setLoading(false); setRefreshing(false); } }
  };

  useEffect(() => { const ac = new AbortController(); fetchAll(ac.signal); fetchActivation(ac.signal); return () => ac.abort(); }, [id]);

  const handleSuspendToggle = () => setShowSuspendConfirm(true);

  const confirmSuspendToggle = async () => {
    setSuspending(true);
    const action = owner?.isSuspended ? 'unsuspend' : 'suspend';
    try {
      await api(`/api/admin/owners/${id}/${action}`, { method: 'POST', body: {} });
      setShowSuspendConfirm(false);
      setInlineMsg({ type: 'success', message: `Owner ${action}ed` });
      fetchAll();
    } catch (err: any) {
      setShowSuspendConfirm(false);
      setInlineMsg({ type: 'error', message: err.message || `Failed to ${action} owner` });
    } finally {
      setSuspending(false);
    }
  };

  const handleResetPassword = async (password: string) => {
    await api(`/api/admin/owners/${id}/reset-password`, {
      method: 'POST',
      body: { password },
    });
  };

  const handleAdminActivate = async () => {
    setActivationLoading(true);
    try {
      await api(`/api/admin/owners/${id}/activation/activate`, { method: 'POST', body: {} });
      setInlineMsg({ type: 'success', message: 'Owner activated' });
      fetchActivation();
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setActivationLoading(false); }
  };

  const handleAdminDeactivate = async () => {
    setActivationLoading(true);
    try {
      await api(`/api/admin/owners/${id}/activation/deactivate`, { method: 'POST', body: {} });
      setInlineMsg({ type: 'success', message: 'Owner deactivated' });
      fetchActivation();
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setActivationLoading(false); }
  };

  const handleWaive = async () => {
    setActivationLoading(true);
    try {
      await api(`/api/admin/owners/${id}/activation/waive`, {
        method: 'POST',
        body: { type: waiveType, months: waiveType === 'months' ? parseInt(waiveMonths) : undefined, note: waiveNote || undefined },
      });
      setShowWaiveForm(false);
      setInlineMsg({ type: 'success', message: 'Fee waived' });
      fetchActivation();
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setActivationLoading(false); }
  };

  const [showRefundConfirm, setShowRefundConfirm] = useState(false);

  const handleRefundActivation = async () => {
    setActivationLoading(true);
    try {
      await api(`/api/admin/owners/${id}/activation/refund`, { method: 'POST', body: {} });
      setShowRefundConfirm(false);
      setInlineMsg({ type: 'success', message: 'Subscription refunded and canceled' });
      fetchActivation();
    } catch (err: any) {
      setShowRefundConfirm(false);
      setInlineMsg({ type: 'error', message: err.message });
    }
    finally { setActivationLoading(false); }
  };

  const handleRemoveWaiver = async () => {
    setActivationLoading(true);
    try {
      await api(`/api/admin/owners/${id}/activation/remove-waiver`, { method: 'POST', body: {} });
      setInlineMsg({ type: 'success', message: 'Waiver removed' });
      fetchActivation();
    } catch (err: any) { setInlineMsg({ type: 'error', message: err.message }); }
    finally { setActivationLoading(false); }
  };

  if (loading) return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  if (!owner) return <View style={styles.center}><Text style={styles.empty}>Owner not found</Text></View>;

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchAll(); }} tintColor={colors.primary} />}
    >
      {/* Header */}
      <TouchableOpacity style={styles.backBtn} onPress={() => router.back()}>
        <Ionicons name="arrow-back" size={20} color={colors.primary} />
        <Text style={styles.backText}>Back to Owners</Text>
      </TouchableOpacity>

      <View style={styles.headerCard}>
        <View style={styles.headerTop}>
          <View style={styles.headerAvatar}>
            <Ionicons name="business" size={28} color="#733e24" />
          </View>
          <View style={styles.headerInfo}>
            <Text style={styles.headerName}>{owner.businessName}</Text>
            <Text style={styles.headerMeta}>{owner.email} · #{owner.accountNumber}</Text>
            <View style={styles.headerBadges}>
              {owner.isSuspended && (
                <View style={[styles.badge, { backgroundColor: '#dc262615' }]}>
                  <Text style={[styles.badgeText, { color: '#dc2626' }]}>Suspended</Text>
                </View>
              )}
              {/* Acquisition milestones — mirrors Admin > Acquisition funnel chips */}
              <StageBadge label="Signed up" on />
              <StageBadge label="Stripe" on={owner.stripeOnboarded} />
              <StageBadge label="Location" on={owner.hasLocation} />
              <StageBadge label="Controller" on={owner.hasController} />
              <StageBadge label="Activated" on={owner.activated} />
              <StageBadge label="Revenue" on={!!owner.firstRevenueAt} />
            </View>
          </View>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.actionBtn} onPress={() => router.push(`/admin/scan?ownerId=${id}` as never)}>
            <Ionicons name="car-sport" size={16} color={colors.primary} />
            <Text style={styles.actionText}>Start Wash</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionBtn} onPress={() => setShowResetPw(true)}>
            <Ionicons name="key" size={16} color={colors.primary} />
            <Text style={styles.actionText}>Reset Password</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionBtn} onPress={() => {
            const url = `${config.appUrl}/vendor/${owner.accountNumber}`;
            openExternal(url);
          }}>
            <Ionicons name="globe-outline" size={16} color={colors.primary} />
            <Text style={styles.actionText}>Vendor Page</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.actionBtn, owner.isSuspended ? undefined : styles.dangerBtn]} onPress={handleSuspendToggle}>
            <Ionicons name={owner.isSuspended ? 'play-circle' : 'pause-circle'} size={16} color={owner.isSuspended ? colors.primary : colors.danger} />
            <Text style={[styles.actionText, owner.isSuspended ? undefined : { color: colors.danger }]}>{owner.isSuspended ? 'Unsuspend' : 'Suspend'}</Text>
          </TouchableOpacity>
        </View>
        <InlineConfirm
          message={`${owner.isSuspended ? 'Unsuspend' : 'Suspend'} ${owner.businessName}?`}
          visible={showSuspendConfirm}
          onConfirm={confirmSuspendToggle}
          onCancel={() => setShowSuspendConfirm(false)}
          confirmLabel={owner.isSuspended ? 'Unsuspend' : 'Suspend'}
          loading={suspending}
          variant={owner.isSuspended ? undefined : 'danger'}
        />
        <InlineMessage
          type="error"
          message={inlineMsg?.message || ''}
          visible={inlineMsg?.type === 'error'}
          onDismiss={() => setInlineMsg(null)}
          autoDismissMs={5000}
        />
      </View>

      {/* Activation */}
      {activation && (
        <View style={styles.headerCard}>
          <Text style={styles.sectionTitle}>Activation</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: spacing.sm }}>
            <View style={[styles.badge, {
              backgroundColor: activation.status === 'active' ? '#05966915' : activation.status === 'grace_period' ? '#d9770615' : '#dc262615',
            }]}>
              <Text style={[styles.badgeText, {
                color: activation.status === 'active' ? '#059669' : activation.status === 'grace_period' ? '#d97706' : '#dc2626',
              }]}>
                {activation.status === 'active' ? 'Active' : activation.status === 'grace_period' ? 'Grace Period' : 'Inactive'}
              </Text>
            </View>
            {activation.waiverType && (
              <View style={[styles.badge, { backgroundColor: '#8b5cf615' }]}>
                <Text style={[styles.badgeText, { color: '#7c3aed' }]}>
                  Waived ({activation.waiverType}{activation.waiverType === 'months' ? ` — ${activation.waiverMonthsRemaining} mo left` : ''})
                </Text>
              </View>
            )}
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm }}>
            {activation.status !== 'active' && (
              <TouchableOpacity style={styles.actionBtn} onPress={handleAdminActivate} disabled={activationLoading}>
                <Ionicons name="checkmark-circle" size={16} color={colors.primary} />
                <Text style={styles.actionText}>Activate</Text>
              </TouchableOpacity>
            )}
            {activation.status !== 'inactive' && (
              <TouchableOpacity style={[styles.actionBtn, styles.dangerBtn]} onPress={handleAdminDeactivate} disabled={activationLoading}>
                <Ionicons name="close-circle" size={16} color={colors.danger} />
                <Text style={[styles.actionText, { color: colors.danger }]}>Deactivate</Text>
              </TouchableOpacity>
            )}
            {!activation.waiverType && (
              <TouchableOpacity style={styles.actionBtn} onPress={() => setShowWaiveForm(!showWaiveForm)}>
                <Ionicons name="gift" size={16} color={colors.primary} />
                <Text style={styles.actionText}>Waive Fee</Text>
              </TouchableOpacity>
            )}
            {activation.waiverType && (
              <TouchableOpacity style={[styles.actionBtn, styles.dangerBtn]} onPress={handleRemoveWaiver} disabled={activationLoading}>
                <Ionicons name="remove-circle" size={16} color={colors.danger} />
                <Text style={[styles.actionText, { color: colors.danger }]}>Remove Waiver</Text>
              </TouchableOpacity>
            )}
            {activation.stripeSubscriptionId && (
              <TouchableOpacity style={[styles.actionBtn, styles.dangerBtn]} onPress={() => setShowRefundConfirm(true)} disabled={activationLoading}>
                <Ionicons name="card" size={16} color={colors.danger} />
                <Text style={[styles.actionText, { color: colors.danger }]}>Refund</Text>
              </TouchableOpacity>
            )}
          </View>
          <InlineConfirm
            message="Refund the latest subscription payment and cancel the Stripe subscription? Owner will remain active."
            visible={showRefundConfirm}
            onConfirm={handleRefundActivation}
            onCancel={() => setShowRefundConfirm(false)}
            confirmLabel="Refund"
            variant="danger"
            loading={activationLoading}
          />
          {showWaiveForm && (
            <View style={{ marginTop: spacing.sm, padding: spacing.sm, backgroundColor: colors.surfaceSecondary, borderRadius: borderRadius.sm }}>
              <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm }}>
                {(['indefinite', 'months'] as const).map(t => (
                  <TouchableOpacity key={t} onPress={() => setWaiveType(t)}
                    style={{ paddingHorizontal: 12, paddingVertical: 4, borderRadius: 12, backgroundColor: waiveType === t ? colors.primary : colors.surface }}>
                    <Text style={{ fontSize: fontSize.xs, color: waiveType === t ? colors.white : colors.text }}>{t}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              {waiveType === 'months' && (
                <TextInput
                  style={{ backgroundColor: colors.surface, borderRadius: 6, padding: 8, fontSize: fontSize.sm, color: colors.text, marginBottom: spacing.sm, borderWidth: 1, borderColor: colors.border }}
                  placeholder="Number of months"
                  placeholderTextColor={colors.textLight}
                  value={waiveMonths}
                  onChangeText={setWaiveMonths}
                  keyboardType="number-pad"
                  maxLength={3}
                />
              )}
              <TextInput
                style={{ backgroundColor: colors.surface, borderRadius: 6, padding: 8, fontSize: fontSize.sm, color: colors.text, marginBottom: spacing.sm, borderWidth: 1, borderColor: colors.border }}
                placeholder="Note (optional)"
                placeholderTextColor={colors.textLight}
                value={waiveNote}
                onChangeText={setWaiveNote}
                maxLength={200}
              />
              <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                <Button title="Cancel" onPress={() => setShowWaiveForm(false)} variant="ghost" size="sm" />
                <Button title="Waive" onPress={handleWaive} loading={activationLoading} size="sm" />
              </View>
            </View>
          )}
        </View>
      )}

      {/* Referral attribution */}
      <View style={styles.headerCard}>
        <Text style={styles.sectionTitle}>Referral</Text>
        <Text style={{ fontSize: fontSize.sm, color: colors.text }}>
          {owner.referral
            ? <>Referred by <Text style={{ fontWeight: fontWeight.semibold }}>{owner.referral.displayName}</Text> (code “{owner.referral.code}”, {owner.referral.type === 'sales_rep' ? 'sales rep' : 'owner'})</>
            : 'No referral attribution'}
        </Text>
        {owner.referralAttributedAt ? (
          <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
            Attributed {new Date(owner.referralAttributedAt).toLocaleDateString()}
          </Text>
        ) : null}
        {owner.ownReferralCode ? (
          <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
            This owner&apos;s own referral code: {owner.ownReferralCode}
          </Text>
        ) : null}
        {hasReferralWrite && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm }}>
            <TouchableOpacity style={styles.actionBtn} onPress={() => { setReferralInput(owner.referral?.code || ''); setShowReferralForm(!showReferralForm); }}>
              <Ionicons name="pricetag" size={16} color={colors.primary} />
              <Text style={styles.actionText}>{owner.referral ? 'Change' : 'Set Referral'}</Text>
            </TouchableOpacity>
            {owner.referral && (
              <TouchableOpacity style={[styles.actionBtn, styles.dangerBtn]} onPress={() => handleReferralSave(null)} disabled={referralSaving}>
                <Ionicons name="close-circle" size={16} color={colors.danger} />
                <Text style={[styles.actionText, { color: colors.danger }]}>Clear</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        {showReferralForm && (
          <View style={{ marginTop: spacing.sm, padding: spacing.sm, backgroundColor: colors.surfaceSecondary, borderRadius: borderRadius.sm }}>
            <TextInput
              style={{ backgroundColor: colors.surface, borderRadius: 6, padding: 8, fontSize: fontSize.sm, color: colors.text, marginBottom: spacing.sm, borderWidth: 1, borderColor: colors.border }}
              placeholder="Referral code (e.g. brandon)"
              placeholderTextColor={colors.textLight}
              value={referralInput}
              onChangeText={(t) => setReferralInput(t.toLowerCase())}
              autoCapitalize="none"
              autoCorrect={false}
              maxLength={32}
            />
            <View style={{ flexDirection: 'row', gap: spacing.sm }}>
              <Button title="Cancel" onPress={() => setShowReferralForm(false)} variant="ghost" size="sm" />
              <Button title="Save" onPress={() => handleReferralSave(referralInput.trim())} loading={referralSaving} size="sm" disabled={!referralInput.trim()} />
            </View>
          </View>
        )}

        {/* Rep commission for this owner: rep default vs per-owner override (mig 0073) */}
        {owner.referral?.type === 'sales_rep' && (
          <View style={{ marginTop: spacing.md, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.borderLight }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.text }}>Rep commission on this customer</Text>
            <Text style={{ fontSize: fontSize.sm, color: colors.text, marginTop: 2 }}>
              {owner.commissionRateBpsOverride != null
                ? <>Override: <Text style={{ fontWeight: fontWeight.semibold }}>{fmtRateBps(owner.commissionRateBpsOverride)}</Text> for {owner.commissionDurationMonthsOverride != null ? `${owner.commissionDurationMonthsOverride} months` : 'forever'}</>
                : owner.referral.commissionRateBps != null
                  ? <>Rep default: {fmtRateBps(owner.referral.commissionRateBps)} for {owner.referral.commissionDurationMonths != null ? `${owner.referral.commissionDurationMonths} months` : 'forever'}</>
                  : 'No commission configured for this rep'}
            </Text>
            {owner.commissionOverrideSetAt ? (
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
                Override set {new Date(owner.commissionOverrideSetAt).toLocaleDateString()}
              </Text>
            ) : null}
            {hasReferralWrite && (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm }}>
                <TouchableOpacity
                  style={styles.actionBtn}
                  onPress={() => {
                    setOverrideRateInput(owner.commissionRateBpsOverride != null ? String(owner.commissionRateBpsOverride / 100) : '');
                    setOverrideDurationInput(owner.commissionDurationMonthsOverride != null ? String(owner.commissionDurationMonthsOverride) : '');
                    setShowOverrideForm(!showOverrideForm);
                  }}
                >
                  <Ionicons name="cash" size={16} color={colors.primary} />
                  <Text style={styles.actionText}>{owner.commissionRateBpsOverride != null ? 'Change Override' : 'Set Override'}</Text>
                </TouchableOpacity>
                {owner.commissionRateBpsOverride != null && (
                  <TouchableOpacity style={[styles.actionBtn, styles.dangerBtn]} onPress={() => handleOverrideSave(null, null)} disabled={overrideSaving}>
                    <Ionicons name="close-circle" size={16} color={colors.danger} />
                    <Text style={[styles.actionText, { color: colors.danger }]}>Clear Override</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
            {showOverrideForm && (
              <View style={{ marginTop: spacing.sm, padding: spacing.sm, backgroundColor: colors.surfaceSecondary, borderRadius: borderRadius.sm }}>
                <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm }}>
                  <TextInput
                    style={{ flex: 1, backgroundColor: colors.surface, borderRadius: 6, padding: 8, fontSize: fontSize.sm, color: colors.text, borderWidth: 1, borderColor: colors.border }}
                    placeholder="Rate % (e.g. 5)"
                    placeholderTextColor={colors.textLight}
                    value={overrideRateInput}
                    onChangeText={setOverrideRateInput}
                    keyboardType="decimal-pad"
                    maxLength={6}
                  />
                  <TextInput
                    style={{ flex: 1, backgroundColor: colors.surface, borderRadius: 6, padding: 8, fontSize: fontSize.sm, color: colors.text, borderWidth: 1, borderColor: colors.border }}
                    placeholder="Months (blank = forever)"
                    placeholderTextColor={colors.textLight}
                    value={overrideDurationInput}
                    onChangeText={setOverrideDurationInput}
                    keyboardType="number-pad"
                    maxLength={4}
                  />
                </View>
                <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginBottom: spacing.sm }}>
                  Beats the rep's default rate for this customer only. Duration counts from this customer's activation. Accrues from today forward.
                </Text>
                <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                  <Button title="Cancel" onPress={() => setShowOverrideForm(false)} variant="ghost" size="sm" />
                  <Button title="Save" onPress={submitOverrideForm} loading={overrideSaving} size="sm" disabled={!overrideRateInput.trim()} />
                </View>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Revenue */}
      {revenue && (
        <>
          <Text style={styles.sectionTitle}>Revenue</Text>
          <View style={styles.revenueGrid}>
            <RevenueCard label="Today" data={revenue.today} fleet={revenue.fleetToday} styles={styles} />
            <RevenueCard label="This Week" data={revenue.thisWeek} fleet={revenue.fleetThisWeek} styles={styles} />
            <RevenueCard label="This Month" data={revenue.thisMonth} fleet={revenue.fleetThisMonth} styles={styles} />
            <RevenueCard label="Last Month" data={revenue.lastMonth} fleet={revenue.fleetLastMonth} styles={styles} />
            <RevenueCard label="Year to Date" data={revenue.yearToDate} fleet={revenue.fleetYearToDate} styles={styles} />
            <RevenueCard label="All Time" data={revenue.allTime} fleet={revenue.fleetAllTime} styles={styles} />
          </View>
        </>
      )}

      {/* Locations */}
      <Text style={styles.sectionTitle}>Locations ({owner.locations.length})</Text>
      {owner.locations.map(loc => {
        const stat = ownerDashboard?.locationStats.find(s => s.locationId === loc.id);
        return (
          <TouchableOpacity key={loc.id} style={styles.locRow} onPress={() => router.push(`/admin/locations?ownerId=${id}` as never)}>
            <Ionicons name="location" size={16} color={loc.isActive ? colors.primary : colors.textLight} />
            <Text style={styles.locName}>{loc.name}</Text>
            <Text style={styles.locCity}>{loc.city}, {loc.state}</Text>
            {stat && stat.count > 0 && (
              <Text style={styles.locCity}>
                · {stat.count} washes · {fmt(stat.totalCharged)}
                {stat.activeSessionsCount > 0 ? ` · ${stat.activeSessionsCount} live` : ''}
                {stat.averageRating != null ? ` · ★${stat.averageRating.toFixed(1)}` : ''}
              </Text>
            )}
            {!loc.isActive && <Text style={styles.locInactive}>Inactive</Text>}
            <Ionicons name="chevron-forward" size={16} color={colors.textLight} />
          </TouchableOpacity>
        );
      })}
      {owner.locations.length === 0 && <Text style={styles.empty}>No locations</Text>}

      {/* Recent Transactions */}
      <Text style={styles.sectionTitle}>Recent Transactions</Text>
      {transactions.map(tx => (
        <View key={tx.id} style={styles.txRow}>
          <Text style={styles.txAmount}>{fmt(tx.amount)}</Text>
          <Text style={styles.txMeta}>{tx.type} · {tx.status} · {new Date(tx.createdAt).toLocaleDateString()}</Text>
        </View>
      ))}
      {transactions.length === 0 && <Text style={styles.empty}>No transactions</Text>}

      {/* Quick Links — per-owner views via ?ownerId filter, or admin per-owner subroutes */}
      <Text style={styles.sectionTitle}>Quick Links</Text>
      <View style={styles.linksRow}>
        {[
          { label: 'Locations', route: 'locations' },
          { label: 'Attendants', route: 'attendants' },
          { label: 'Transactions', route: 'transactions' },
          { label: 'Refunds', route: 'refunds' },
          { label: 'Vouchers', route: 'vouchers' },
          { label: 'Fleet Accounts', route: 'fleet-accounts' },
          { label: 'Fleet Invoices', route: 'fleet-invoices' },
          { label: 'Fleet Pricing Tiers', route: `owner/${id}/fleet-tiers`, noOwnerQs: true },
          { label: 'Fleet Applications', route: `owner/${id}/fleet-applications`, noOwnerQs: true },
          { label: 'Subscriptions & Rewards', route: `owner/${id}/subscriptions`, noOwnerQs: true },
          { label: 'Staff', route: `owner/${id}/staff`, noOwnerQs: true },
          { label: 'Hardware Orders', route: `owner/${id}/shop/orders`, noOwnerQs: true },
          { label: 'Owner Settings', route: `owner/${id}/settings`, noOwnerQs: true },
          { label: 'Email Alerts', route: `owner/${id}/email-alerts`, noOwnerQs: true },
          { label: 'API Access', route: `owner/${id}/api-access`, noOwnerQs: true }, // 1.4 P28
          { label: 'Wash Tiers & Upsells', route: `owner/${id}/pricing/washes-upsells`, noOwnerQs: true },
          { label: 'Payroll', route: `owner/${id}/payroll`, noOwnerQs: true },
          { label: 'Programs (Gifts/Promos/Points/Winback)', route: `owner/${id}/programs`, noOwnerQs: true },
          { label: 'Controllers', route: 'controllers' },
        ].map(link => (
          <TouchableOpacity key={link.route} style={styles.linkBtn}
            onPress={() => router.push(`/admin/${link.route}${(link as any).noOwnerQs ? '' : `?ownerId=${id}`}` as never)}>
            <Text style={styles.linkText}>{link.label}</Text>
            <Ionicons name="arrow-forward" size={14} color={colors.primary} />
          </TouchableOpacity>
        ))}
      </View>

      <ResetPasswordModal
        visible={showResetPw}
        onClose={() => setShowResetPw(false)}
        onSubmit={handleResetPassword}
        targetName={owner.businessName}
      />
      <PageFooter />
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.lg, paddingBottom: spacing.xxl },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: spacing.md },
  backText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  headerCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.md,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1,
  },
  headerTop: { flexDirection: 'row', gap: spacing.md, marginBottom: spacing.md },
  headerAvatar: { width: 56, height: 56, borderRadius: 28, backgroundColor: '#f0e4db', justifyContent: 'center', alignItems: 'center' },
  headerInfo: { flex: 1 },
  headerName: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  headerMeta: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },
  headerBadges: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs, alignItems: 'center' },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  headerActions: { flexDirection: 'row', gap: spacing.sm },
  actionBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.sm, backgroundColor: c.primaryBg,
  },
  dangerBtn: { backgroundColor: '#dc262610' },
  actionText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.primary },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.md, marginBottom: spacing.sm },
  revenueGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  revenueCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    width: '31%', minWidth: 100, alignItems: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1,
  },
  revenueValue: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  revenueLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  revenueSub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 1 },
  locRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  locName: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, flex: 1 },
  locCity: { fontSize: fontSize.xs, color: c.textLight },
  locInactive: { fontSize: fontSize.xs, color: c.danger },
  txRow: { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  txAmount: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.text },
  txMeta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  linkBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.sm, backgroundColor: c.surface,
    borderWidth: 1, borderColor: c.border,
  },
  linkText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  empty: { textAlign: 'center', color: c.textSecondary, paddingVertical: spacing.md, fontSize: fontSize.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
